import { useCallback, useEffect, useState } from 'react';
import { KpiTile, PageHeader, SectionCard, TabBar } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';

type NotificationKpis = {
  kpis: {
    total_sent: number;
    delivered: number;
    failed: number;
    pending: number;
    consent_blocked: number;
  };
  by_channel: Array<{ channel: string; count: number; delivered: number; failed: number }>;
  generated_at: string;
};

type NotificationTemplate = {
  id: number;
  tenant_id: number | null;
  code: string;
  channel: 'email' | 'sms' | 'webhook';
  subject_tpl: string | null;
  body_tpl: string;
  html_tpl: string | null;
  variables_schema: Array<{ key: string; description: string; required: boolean }>;
  routing_rules: { require_consent: boolean; fallback_channel: string | null };
  is_active: boolean;
};

type NotificationLogEntry = {
  id: number;
  tenant_id: number;
  template_id: number | null;
  template_code: string;
  channel: string;
  recipient: string;
  subject: string | null;
  body: string;
  status: string;
  outbox_id: number | null;
  consent_checked: boolean;
  consent_granted: boolean | null;
  error_message: string | null;
  created_at: string;
  sent_at: string | null;
};

type ConsentEntry = {
  id: number;
  end_user_id: number;
  consent_type: string;
  granted: boolean;
  granted_at: string;
  revoked_at: string | null;
  source: string | null;
};

type Tab = 'overview' | 'templates' | 'log' | 'consent';

const STATUS_TONE: Record<string, string> = {
  delivered: 'ok',
  sent: 'ok',
  failed: 'error',
  pending: 'warn',
  queued: 'warn',
  consent_blocked: 'warn',
};

export function MasterNotificationsPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [kpis, setKpis] = useState<NotificationKpis | null>(null);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [logEntries, setLogEntries] = useState<NotificationLogEntry[]>([]);
  const [consents, setConsents] = useState<ConsentEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logFilter, setLogFilter] = useState('');
  const [logStatusFilter, setLogStatusFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [kpiRes, tplRes, logRes, conRes] = await Promise.all([
        apiFetch('/api/v1/master/notifications/kpis'),
        apiFetch('/api/v1/master/notifications/templates'),
        apiFetch('/api/v1/master/notifications/log?limit=50'),
        apiFetch('/api/v1/master/notifications/consent?limit=50'),
      ]);
      if (kpiRes.ok) {
        const body = (await kpiRes.json().catch(() => null)) as NotificationKpis | null;
        if (body) setKpis(body);
      }
      if (tplRes.ok) {
        const body = (await tplRes.json().catch(() => null)) as { templates?: NotificationTemplate[] } | null;
        if (body?.templates) setTemplates(body.templates);
      }
      if (logRes.ok) {
        const body = (await logRes.json().catch(() => null)) as { entries?: NotificationLogEntry[] } | null;
        if (body?.entries) setLogEntries(body.entries);
      }
      if (conRes.ok) {
        const body = (await conRes.json().catch(() => null)) as { consents?: ConsentEntry[] } | null;
        if (body?.consents) setConsents(body.consents);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function reloadLog() {
    const params = new URLSearchParams({ limit: '50' });
    if (logFilter) params.set('tenantId', logFilter);
    if (logStatusFilter) params.set('status', logStatusFilter);
    const res = await apiFetch(`/api/v1/master/notifications/log?${params}`);
    if (res.ok) {
      const body = (await res.json().catch(() => null)) as { entries?: NotificationLogEntry[] } | null;
      if (body?.entries) setLogEntries(body.entries);
    }
  }

  const k = kpis?.kpis;

  return (
    <section className="sk-mc-page">
      <PageHeader
        title="Notifiche"
        subtitle="Template engine, log consegne, invio manuale, GDPR consent."
        actions={
          <button type="button" className="sk-btn sk-btn--quiet" onClick={() => void load()}>
            Aggiorna
          </button>
        }
      />

      {error ? <p className="sk-error">{error}</p> : null}

      <TabBar<Tab>
        tabs={[
          { key: 'overview', label: 'Overview' },
          { key: 'templates', label: 'Template', count: templates.length },
          { key: 'log', label: 'Log', count: logEntries.length },
          { key: 'consent', label: 'Consent', count: consents.length },
        ]}
        active={tab}
        onChange={setTab}
      />

      {/* Tab: Overview */}
      {tab === 'overview' ? (
        <div>
          <div className="sk-mc-grid sk-mc-grid--kpi">
            <KpiTile
              label="Inviati (30gg)"
              value={k?.total_sent ?? '—'}
              sub={loading ? 'Caricamento…' : 'Ultimi 30 giorni'}
              tone="info"
              icon={<span>📨</span>}
            />
            <KpiTile
              label="Consegnati"
              value={k?.delivered ?? 0}
              sub={k ? `${Math.round((k.delivered / Math.max(k.total_sent, 1)) * 100)}% delivery` : ''}
              tone="ok"
              icon={<span>✓</span>}
            />
            <KpiTile
              label="Falliti"
              value={k?.failed ?? 0}
              sub={k && k.failed > 0 ? 'Richiede attenzione' : 'Tutto OK'}
              tone={k && k.failed > 0 ? 'error' : 'ok'}
              icon={<span>✕</span>}
            />
            <KpiTile
              label="Consent blocked"
              value={k?.consent_blocked ?? 0}
              sub="GDPR opt-out"
              tone={k && k.consent_blocked > 0 ? 'warn' : 'neutral'}
              icon={<span>🔒</span>}
            />
          </div>

          {kpis && kpis.by_channel.length > 0 ? (
            <SectionCard title="Per canale">
              <div className="sk-data-table-wrap">
                <table className="sk-data-table">
                  <thead>
                    <tr>
                      <th>Canale</th>
                      <th>Totale</th>
                      <th>Consegnati</th>
                      <th>Falliti</th>
                      <th>Delivery %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kpis.by_channel.map((c) => (
                      <tr key={c.channel}>
                        <td><strong>{c.channel}</strong></td>
                        <td>{c.count}</td>
                        <td>{c.delivered}</td>
                        <td>{c.failed}</td>
                        <td>{c.count > 0 ? Math.round((c.delivered / c.count) * 100) : 0}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          ) : null}
        </div>
      ) : null}

      {/* Tab: Templates */}
      {tab === 'templates' ? (
        <SectionCard title="Template di sistema (defaults)">
          {templates.length === 0 ? (
            <p className="sk-mc-hint">Nessun template di sistema.</p>
          ) : (
            <div className="sk-data-table-wrap">
              <table className="sk-data-table">
                <thead>
                  <tr>
                    <th>Codice</th>
                    <th>Canale</th>
                    <th>Subject</th>
                    <th>Body (anteprima)</th>
                    <th>Consent</th>
                    <th>Attivo</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={`${t.id}`}>
                      <td><strong>{t.code}</strong></td>
                      <td>{t.channel}</td>
                      <td>{t.subject_tpl || '—'}</td>
                      <td className="sk-mc-cell-mono">{t.body_tpl.slice(0, 80)}{t.body_tpl.length > 80 ? '…' : ''}</td>
                      <td>{t.routing_rules?.require_consent ? 'sì' : 'no'}</td>
                      <td>{t.is_active ? '✓' : '✕'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      ) : null}

      {/* Tab: Log */}
      {tab === 'log' ? (
        <SectionCard
          title="Log consegne"
          actions={
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                className="sk-input"
                placeholder="Tenant ID"
                value={logFilter}
                onChange={(e) => setLogFilter(e.target.value)}
                style={{ width: '100px' }}
              />
              <select
                className="sk-input"
                value={logStatusFilter}
                onChange={(e) => setLogStatusFilter(e.target.value)}
                style={{ width: '120px' }}
              >
                <option value="">Tutti gli status</option>
                <option value="delivered">Delivered</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
                <option value="consent_blocked">Consent blocked</option>
              </select>
              <button type="button" className="sk-btn sk-btn--quiet" onClick={() => void reloadLog()}>
                Filtra
              </button>
            </div>
          }
        >
          {logEntries.length === 0 ? (
            <p className="sk-mc-hint">Nessuna notifica nel log.</p>
          ) : (
            <div className="sk-data-table-wrap">
              <table className="sk-data-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Tenant</th>
                    <th>Template</th>
                    <th>Canale</th>
                    <th>Destinatario</th>
                    <th>Status</th>
                    <th>Consent</th>
                    <th>Errore</th>
                  </tr>
                </thead>
                <tbody>
                  {logEntries.map((e) => (
                    <tr key={e.id}>
                      <td>{new Date(e.created_at).toLocaleString('it-IT')}</td>
                      <td>{e.tenant_id}</td>
                      <td className="sk-mc-cell-mono">{e.template_code}</td>
                      <td>{e.channel}</td>
                      <td className="sk-mc-cell-mono">{e.recipient}</td>
                      <td>
                        <span className={`sk-mc-status sk-mc-status--${STATUS_TONE[e.status] || 'neutral'}`}>
                          {e.status}
                        </span>
                      </td>
                      <td>{e.consent_checked ? (e.consent_granted ? '✓' : '✕') : '—'}</td>
                      <td className="sk-mc-cell-mono">{e.error_message ? e.error_message.slice(0, 50) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      ) : null}

      {/* Tab: Consent */}
      {tab === 'consent' ? (
        <SectionCard title="GDPR Consent (recenti)">
          {consents.length === 0 ? (
            <p className="sk-mc-hint">Nessun consenso registrato.</p>
          ) : (
            <div className="sk-data-table-wrap">
              <table className="sk-data-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>End User</th>
                    <th>Tipo</th>
                    <th>Granted</th>
                    <th>Revocato</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {consents.map((c) => (
                    <tr key={c.id}>
                      <td>{new Date(c.granted_at).toLocaleString('it-IT')}</td>
                      <td>{c.end_user_id}</td>
                      <td className="sk-mc-cell-mono">{c.consent_type}</td>
                      <td>
                        <span className={`sk-mc-status sk-mc-status--${c.granted ? 'ok' : 'error'}`}>
                          {c.granted ? 'granted' : 'denied'}
                        </span>
                      </td>
                      <td>{c.revoked_at ? new Date(c.revoked_at).toLocaleDateString('it-IT') : '—'}</td>
                      <td>{c.source || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      ) : null}
    </section>
  );
}
